/**
 * Projected contract value over 24 months — the ops-side analogue of Cinch's
 * contract-value chart. A stepped monthly-billed area with a prorated P1 stub, M1..M24
 * full months, purple dashed term/renewal boundaries drawn BETWEEN periods, and green
 * dashed upsell markers. Replacement contracts get a muted PC (Previous Contract) lead-in.
 *
 * A numeric X axis (idx) is what lets boundary lines land at `idx + 0.5` — between two
 * periods — rather than on a period (the confusing placement we're moving away from).
 */

import { useMemo } from "react";

import {
  Area,
  CartesianGrid,
  ComposedChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { fmtCur } from "../../renewals/format";
import type { ContractDetail } from "../api";
import { CONTRACT_CHART_COLORS as C } from "../chartColors";
import {
  buildContractProjection,
  buildReplacementProjection,
  type ProjectionPoint,
} from "../projection";

interface Props {
  contract: ContractDetail;
  /** The replaced contract (full detail), when `contract` is a replacement — drives the PC lead-in. */
  previous?: ContractDetail | null;
}

/** Two-line X tick: period key (PC / P1 / M1 …) over its MMM-YYYY. Calendar labels are
 *  thinned (every 3rd, plus PC columns and the last) so 30 columns don't overlap. */
function PeriodTick(props: {
  x?: number;
  y?: number;
  payload?: { value: number };
  points: ProjectionPoint[];
}) {
  const { x = 0, y = 0, payload, points } = props;
  const p = points.find((pt) => pt.idx === payload?.value);
  if (!p) return null;
  const showCal = p.isPrevious || p.idx % 3 === 0 || p.idx === points.length - 1;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={11} textAnchor="middle" fontSize={10} fill={p.isPrevious ? C.previous : "currentColor"}>
        {p.key}
      </text>
      {showCal && (
        <text x={0} y={0} dy={23} textAnchor="middle" fontSize={9} fill="hsl(215 16% 47%)">
          {p.calendarLabel}
        </text>
      )}
    </g>
  );
}

export function ContractTimelineChart({ contract, previous }: Props) {
  const projection = useMemo(
    () =>
      contract.replacement_contract_details
        ? buildReplacementProjection(contract, previous)
        : buildContractProjection(contract),
    [contract, previous],
  );

  const { points, boundaries, upsellMarkers, baseMonthly, total, firstYear, hasStub, pcCount } =
    projection;
  const lastIdx = points.length - 1;
  const calByIdx = useMemo(() => new Map(points.map((p) => [p.idx, p])), [points]);

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
        <CardTitle className="text-sm">Projected Value (24 mo)</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="neutral">Base {fmtCur(baseMonthly)}/mo</Badge>
          <Badge variant="neutral">1st 12mo{hasStub ? " (incl. P1)" : ""} {fmtCur(firstYear)}</Badge>
          <Badge variant="info">Total {fmtCur(total)}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">
          Assumes the deal is kept for 24 months with no changes. The line steps at term /
          renewal boundaries (purple, between periods) and when time-limited discounts roll off;
          green markers are upsell amendments.
          {pcCount > 0
            ? " PC columns are the previous contract this one replaced (the last months it billed)."
            : ""}
          {hasStub ? " P1 is the day-prorated first period (billing-effective date → month-end)." : ""}
        </p>
        <div className="h-80">
          <ResponsiveContainer>
            <ComposedChart data={points} margin={{ top: 10, right: 16, bottom: 14, left: 8 }}>
              <CartesianGrid stroke={C.gridLine} strokeDasharray="2 3" />
              {pcCount > 0 && (
                <ReferenceArea
                  x1={-0.5}
                  x2={pcCount - 0.5}
                  fill={C.previous}
                  fillOpacity={0.06}
                  ifOverflow="extendDomain"
                />
              )}
              <XAxis
                type="number"
                dataKey="idx"
                domain={[-0.5, lastIdx + 0.5]}
                ticks={points.map((p) => p.idx)}
                interval={0}
                tick={<PeriodTick points={points} />}
                height={36}
              />
              <YAxis
                tick={{ fontSize: 10 }}
                width={68}
                tickFormatter={(v: number) => `$${Number(v).toLocaleString()}`}
              />
              <Tooltip
                formatter={(v) => [fmtCur(Number(v)), "Billed / mo"]}
                labelFormatter={(idx: number) => {
                  const p = calByIdx.get(idx);
                  if (!p) return String(idx);
                  const tag = p.isPrevious ? " · Previous contract" : "";
                  return `${p.key} · ${p.calendarLabel}${tag}`;
                }}
              />
              {boundaries.map((b) => (
                <ReferenceLine
                  key={`term-${b.idx}-${b.label}`}
                  x={b.idx + 0.5}
                  stroke={C.term}
                  strokeDasharray="6 4"
                  label={{ value: b.label, position: "insideTopLeft", fontSize: 10, fill: C.term }}
                />
              ))}
              {upsellMarkers.map((u) => (
                <ReferenceLine
                  key={`upsell-${u.idx}-${u.label}`}
                  x={u.idx}
                  stroke={C.upsell}
                  strokeDasharray="4 4"
                  label={{ value: u.label, position: "insideTopRight", fontSize: 10, fill: C.upsell }}
                />
              ))}
              <Area
                type="stepAfter"
                dataKey="billed"
                stroke={C.brand}
                strokeWidth={2}
                fill={C.brand}
                fillOpacity={0.12}
                dot={false}
                name="Billed / mo"
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
